import { once } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, Recipient, RecordData, Service, Slack } from 'webapp-types';
import {
	DeliveryType,
	ModelName,
	ScorecardType,
	type DayOfTheWeek,
	type ScheduleType,
} from 'webapp/enums/enums';
import { sortBy, uniq } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';

import type { UserRecord } from 'webapp/models';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import Schedule from 'webapp/models-custom/schedule';
import type ScorecardRecord from 'webapp/models/scorecard';

import { Severity } from 'webapp/services/logger';

export default class Scorecard extends BufferedModel<
	ScorecardRecord,
	RecordData.Scorecard.Serialized
> {
	@service declare constants: Service.Constants;
	@service('logger') declare loggerService: Service.Logger;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	get id(): string | null {
		return this.model.id;
	}

	get title(): string {
		return this.getBuffered('title');
	}

	set title(value: string) {
		this.setBuffered('title', value);
	}

	get color(): string {
		return this.getBuffered('color');
	}

	set color(value: string) {
		this.setBuffered('color', value);
	}

	get blockId(): number | null {
		return this.getBuffered('blockId');
	}

	set blockId(value: number | null) {
		this.setBuffered('blockId', value);
	}

	get createdBy(): number {
		return this.getBuffered('createdBy');
	}

	set createdBy(value: number) {
		this.setBuffered('createdBy', value);
	}

	get created(): number {
		return this.getBuffered('created');
	}

	get delivery(): number[] {
		return this.getBuffered('delivery');
	}

	set delivery(value: number[]) {
		this.setBuffered('delivery', value);
	}

	get slackChannels(): Slack.Channel[] {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channel[]) {
		this.setBuffered('slackChannels', value);
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	get userIds(): number[] {
		return this.getBuffered('userIds');
	}

	set userIds(value: number[]) {
		this.setBuffered('userIds', value);
	}

	get originateFrom(): number | null {
		return this.getBuffered('originateFrom');
	}

	set originateFrom(value: number | null) {
		this.setBuffered('originateFrom', value);
	}

	get addNewUsersToDelivery(): boolean {
		return this.getBuffered('addNewUsersToDelivery');
	}

	set addNewUsersToDelivery(value: boolean) {
		this.setBuffered('addNewUsersToDelivery', value);
	}

	get cloneFrom(): number | null {
		return this.getBuffered('cloneFrom');
	}

	set cloneFrom(value: number | null) {
		this.setBuffered('cloneFrom', value);
	}

	get scheduleType(): ScheduleType {
		return this.getBuffered('scheduleType');
	}

	set scheduleType(value) {
		this.setBuffered('scheduleType', value);
		this.schedule.type = value;
	}

	get scheduleHourOfDay() {
		return this.getBuffered('scheduleHourOfDay');
	}

	set scheduleHourOfDay(value) {
		this.setBuffered('scheduleHourOfDay', value);
		this.schedule.hourOfDay = value;
	}

	get scheduleDayOfWeek(): DayOfTheWeek[] {
		return this.getBuffered('scheduleDayOfWeek');
	}

	set scheduleDayOfWeek(value: DayOfTheWeek[]) {
		this.setBuffered('scheduleDayOfWeek', value);
		this.schedule.dayOfWeek = value;
	}

	get scheduleDayOfMonth() {
		return this.getBuffered('scheduleDayOfMonth');
	}

	set scheduleDayOfMonth(value) {
		this.setBuffered('scheduleDayOfMonth', value);
		this.schedule.dayOfMonth = value;
	}

	get isActive(): boolean {
		return this.getBuffered('active');
	}

	set isActive(value: boolean) {
		this.setBuffered('active', value);
		void this.saveAndCatch();
	}

	get type(): ScorecardType {
		return this.getBuffered('type');
	}

	set type(value: ScorecardType) {
		this.setBuffered('type', value);
	}

	override get validationRules(): Record<string, ValidationRule[]> {
		return {
			title: ['required'],
		};
	}

	override get isDirty(): boolean {
		if (super.isDirty) {
			return true;
		}

		const savedDs = this.savedDataSelects,
			currentDs = this.temporaryEditDataSelects.filter((ds) => {
				return !!ds.metricSetupKey;
			});

		return currentDs.length !== savedDs.length || currentDs.some((ds) => ds.isDirty);
	}

	get isDigest() {
		return this.type === ScorecardType.METRIC_DIGEST;
	}

	get createdByUser() {
		let user;
		if (this.createdBy) {
			user = this.storeService.peekRecord(ModelName.USER, this.createdBy) as UserRecord;
			if (!user || !!user.isDeleted) {
				user = null;
				this.logger.logError(
					'Missing createdBy for scorecard',
					{
						scorecardId: this.id,
						userId: this.createdBy,
					},
					Severity.WARNING,
				);
			}
		}
		return user;
	}

	get createdText() {
		const createdByUser = this.createdByUser;
		let text = getFormattedTime(this.created, this.currentUserService.model?.timezone);

		if (createdByUser) {
			text += `<br /> by ${createdByUser.name}`;
		}

		return text;
	}

	get colorStyle() {
		const bgStyle = this.mmBoardsService.getColorGradient(this.color);
		return htmlSafe(bgStyle);
	}

	get isScheduledForMe(): boolean {
		return this.userIds.includes(this.currentUserService.numericId!);
	}

	_limitations: MetricLimitations | null = null;
	get limitations() {
		if (!this._limitations) {
			const dateRange = DateRange.createFromFormula(this.dateRange);
			let compare = this.mmScorecardsService.getCommonCompareFromDsRecordArray(
				this.savedDataSelects.map((ds) => ds.model),
			);

			if (!compare) {
				compare =
					this.mmScorecardsService.createPreviousPeriodCompareForDateRange(dateRange);
			}

			const metrics = this.savedDataSelects
				.filter((dataSelect) => dataSelect.metricData !== null)
				.map((dataSelect) => dataSelect.metricData!);

			const limitations = new MetricLimitations({
				isNumber: true,
				metrics,
				dateRange,
				compareCMs: [compare],
			});

			this._limitations = limitations;
		}
		return this._limitations;
	}

	_schedule: Schedule | null = null;

	get schedule() {
		if (this._schedule) {
			// mark dependents so this getter updates and triggers the schedule update from record
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleType;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleHourOfDay;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfWeek;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.scheduleDayOfMonth;
			once(this._schedule, this._schedule.updateScheduleFromModel, this);
		} else {
			this._schedule = Schedule.createFromModel(this);
		}
		return this._schedule;
	}

	updateScheduleValues() {
		const schedule = this.schedule;
		schedule.updateScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	cleanupUnusedScheduleValues() {
		const schedule = this.schedule;
		schedule.cleanupUnusedScheduleValues();
		schedule.updateModelFromSchedule(this);
	}

	get hasDelivery() {
		return !!this.delivery?.length;
	}

	private _dataSelects: DataSelect[] = [];

	@tracked presetDataSelectsOnCreate: DataSelect[] = [];

	@tracked temporaryEditDataSelects: DataSelect[] = []; // just while form is alive. need for determining dirty & passing to preview

	cleanupTemporaryEditDataSelects() {
		this.temporaryEditDataSelects.forEach((ds) => {
			if (ds.isNew) {
				void ds.delete(true);
			}
		});
	}

	@cached
	get savedDataSelects(): DataSelect[] {
		if (this.blockId) {
			const records = sortBy(
				this.mmDataSelectsService.savedRecords.filter(
					(item) => item.blockId === this.blockId,
				),
				'order',
			);
			this.mmDataSelectsService.updateModelCache(this._dataSelects, records);
			return this._dataSelects;
		} else {
			return this.presetDataSelectsOnCreate;
		}
	}

	get isActiveUserOwner() {
		return !this.createdBy || +this.createdBy === this.currentUserService.numericId;
	}

	get deleteText() {
		let deleteText = '';
		if (this.isActiveUserOwner && this.userIds && this.userIds.length > 1) {
			deleteText =
				'<strong>This scorecard has multiple recipients.</strong> By deleting it, both you and other recipients will stop receiving it.';
		}
		return deleteText;
	}

	get usedMetricsText(): string {
		const metrics: string[] = [];
		this.savedDataSelects.forEach((ds) => {
			metrics.push(ds.metricSetup.selectedMetricTitle);
		});
		return metrics.join(', ');
	}

	get hasOtherRecipientsThanMe() {
		const recipients = this.userIds;
		return (
			recipients?.length &&
			(recipients.length > 1 || !recipients.includes(this.currentUserService.numericId!))
		);
	}

	get mappedRecipients(): Recipient[] {
		return this.mmUsersService.getMappedRecipients([], this.userIds, this.slackChannels);
	}

	get deliveryOptionsCount() {
		return this.delivery.length;
	}

	get deliveriesTexts() {
		return this.delivery.map((d) => {
			switch (d) {
				case DeliveryType.EMAIL:
					return 'email';
				case DeliveryType.PUSH:
					return 'mobile';
				case DeliveryType.SLACK:
					return 'slack';
			}
		});
	}

	get recipientsCount() {
		return this.mappedRecipients.length;
	}

	get titleForSlackList() {
		return this.title;
	}

	get slackChannelCountForSlackList() {
		return this.slackChannels.length;
	}

	get deliveryTimeLevel() {
		return this.schedule.deliveryTimeLevel;
	}

	get recipientsNames() {
		return uniq(this.mappedRecipients.map((item) => item.name));
	}

	get recipientsMails() {
		const emails: string[] = [];
		this.mappedRecipients.forEach((r) => {
			if (r.email) {
				emails.push(r.email);
			}
		});
		return emails;
	}

	get dateRangeCM() {
		return this.limitations.dateRange!;
	}

	get compare() {
		return this.limitations.compare!;
	}

	get dateIntervalText(): string {
		return this.dateRangeCM?.rangeTitleUX || '';
	}

	getDataForClone(includeOriginateFrom = true) {
		const data = this.toJSON();

		data.cloneFrom = this.numericId;
		data.originateFrom = includeOriginateFrom ? this.numericId : null; // when set, BE will remove the original for this user (sends deletedRecords)
		data.title = 'Copy of ' + data.title;
		data.type = this.type;
		data.active = this.isActive;

		delete data.blockId;

		delete data.created;
		delete data.createdBy;

		return data;
	}

	// when date-range formula changed, update the model
	updateDateRangePlainObjFromCM(): void {
		if (!this.dateRangeCM?.isEqual(this.dateRange)) {
			const selectedFormula = this.dateRangeCM?.formula;
			if (selectedFormula) {
				this.dateRange = selectedFormula;
			}
		}
	}

	createAnotherModel() {
		return new Scorecard({ model: this.model });
	}

	override delete(skipBE = false, skipClearConnectedRecords = false) {
		this.cleanupTemporaryEditDataSelects();
		return super.delete(skipBE, skipClearConnectedRecords);
	}

	static override modelName = ModelName.SCORECARD; // store record type
}
